import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useQuote } from '../context/QuoteContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { FileText, Trash2, Plus, Minus, Send, ArrowLeft, ShoppingCart, CheckCircle, Package, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { productImage } from '../utils/imageUtils';
import { generateQuotePdf } from '../utils/generateQuotePdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuoteListPage = () => {
  const { t, language } = useLanguage();
  const { quoteItems, removeFromQuote, updateQuantity, clearQuote } = useQuote();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [formData, setFormData] = useState({
    company: '',
    orgNumber: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) => setSiteSettings(res.data))
      .catch(() => {});
  }, []);

  const handleExportPdf = async () => {
    if (quoteItems.length === 0) {
      toast.error(language === 'sv' ? 'Offertlistan är tom' : 'Quote list is empty');
      return;
    }
    setExportingPdf(true);
    try {
      const { doc, refNumber } = await generateQuotePdf({
        items: quoteItems,
        contact: formData,
        siteSettings,
        language,
      });
      doc.save(`Offert_${refNumber}.pdf`);
      toast.success(language === 'sv' ? 'PDF nedladdad!' : 'PDF downloaded!');
    } catch (err) {
      console.error(err);
      toast.error(language === 'sv' ? 'Kunde inte skapa PDF' : 'Could not create PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quoteItems.length === 0) {
      toast.error(language === 'sv' ? 'Offertlistan är tom' : 'Quote list is empty');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        ...formData,
        items: quoteItems,
        language
      });
      
      toast.success(language === 'sv' ? 'Offertförfrågan skickad!' : 'Quote request sent!');
      setSubmitted(true);
      clearQuote();
    } catch (error) {
      toast.error(language === 'sv' ? 'Kunde inte skicka förfrågan' : 'Could not send request');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#FAFAF8] min-h-screen pt-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white border border-stone-200 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-stone-900 mb-4">
              {language === 'sv' ? 'Tack för din förfrågan!' : 'Thank you for your request!'}
            </h1>
            <p className="text-stone-600 mb-8">
              {language === 'sv' 
                ? 'Vi har mottagit din offertförfrågan och återkommer till dig så snart som möjligt.'
                : 'We have received your quote request and will get back to you as soon as possible.'
              }
            </p>
            <Link to="/produkter">
              <Button className="bg-stone-900 text-white hover:bg-stone-800 rounded-none">
                {language === 'sv' ? 'Fortsätt handla' : 'Continue shopping'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FAFAF8] min-h-screen pt-24" data-testid="quote-list-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/produkter" className="text-stone-500 hover:text-stone-700 text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" />
              {language === 'sv' ? 'Tillbaka till produkter' : 'Back to products'}
            </Link>
            <h1 className="text-3xl font-bold text-stone-900 flex items-center gap-3">
              <FileText className="w-8 h-8" />
              {language === 'sv' ? 'Offertlista' : 'Quote List'}
            </h1>
            <p className="text-stone-600 mt-1">
              {quoteItems.length} {language === 'sv' ? 'produkter' : 'products'}
            </p>
          </div>
          {quoteItems.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleExportPdf}
                disabled={exportingPdf}
                data-testid="export-quote-pdf"
                className="border-stone-300 text-stone-700 hover:bg-stone-100 rounded-none"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingPdf
                  ? (language === 'sv' ? 'Skapar PDF...' : 'Creating PDF...')
                  : (language === 'sv' ? 'Exportera PDF' : 'Export PDF')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  if (window.confirm(language === 'sv' ? 'Rensa hela listan?' : 'Clear entire list?')) {
                    clearQuote();
                  }
                }}
                className="border-red-300 text-red-700 hover:bg-red-50 rounded-none"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {language === 'sv' ? 'Rensa lista' : 'Clear list'}
              </Button>
            </div>
          )}
        </div>

        {quoteItems.length === 0 ? (
          <div className="bg-white border border-stone-200 p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-stone-900 mb-2">
              {language === 'sv' ? 'Din offertlista är tom' : 'Your quote list is empty'}
            </h2>
            <p className="text-stone-600 mb-6">
              {language === 'sv' 
                ? 'Lägg till produkter för att begära en offert'
                : 'Add products to request a quote'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/produkter">
                <Button className="bg-stone-900 text-white hover:bg-stone-800 rounded-none w-full sm:w-auto">
                  {language === 'sv' ? 'Bläddra produkter' : 'Browse products'}
                </Button>
              </Link>
              <Link to="/snabboffert">
                <Button variant="outline" className="border-[#321a27] text-[#321a27] hover:bg-[#f5f0f2] rounded-none w-full sm:w-auto" data-testid="quick-quote-cta">
                  <span className="mr-2">⚡</span>
                  {language === 'sv' ? 'Snabboffert (klistra in artikelnr)' : 'Quick Quote (paste articles)'}
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Product List */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-stone-200">
                <div className="p-4 border-b border-stone-200 bg-stone-50">
                  <h2 className="font-semibold text-stone-900">
                    {language === 'sv' ? 'Valda produkter' : 'Selected products'}
                  </h2>
                </div>
                <div className="divide-y divide-stone-200">
                  {quoteItems.map((item) => (
                    <div key={item.id} className="p-4 flex items-start gap-4">
                      {/* Product Image */}
                      <Link 
                        to={`/produkter/${item.productId}`}
                        className="w-20 h-20 bg-stone-100 shrink-0 flex items-center justify-center overflow-hidden border border-stone-200"
                      >
                        {item.productImage ? (
                          <img 
                            src={productImage(item.productImage)}
                            alt=""
                            className="w-full h-full object-contain p-1 bg-white"
                          />
                        ) : (
                          <Package className="w-8 h-8 text-stone-300" />
                        )}
                      </Link>
                      
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/produkter/${item.productId}`}
                          className="font-medium text-stone-900 hover:text-[#321a27]"
                        >
                          {language === 'sv' ? item.productName_sv : item.productName_en}
                        </Link>
                        {item.variant && (
                          <div className="mt-2 text-sm text-stone-600 space-y-1">
                            {Object.entries(item.variant).map(([key, value]) => (
                              value && (
                                <span key={key} className="inline-block mr-4">
                                  <span className="text-stone-500">{key}:</span> {value}
                                </span>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Quantity controls */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center border border-stone-300 hover:bg-stone-100"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center font-medium">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center border border-stone-300 hover:bg-stone-100"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Remove button */}
                      <button 
                        onClick={() => removeFromQuote(item.id)}
                        className="text-stone-400 hover:text-red-600 p-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Quote Request Form */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-stone-200 sticky top-24">
                <div className="p-4 border-b border-stone-200 bg-stone-50">
                  <h2 className="font-semibold text-stone-900">
                    {language === 'sv' ? 'Begär offert' : 'Request quote'}
                  </h2>
                </div>
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                  <div>
                    <Label>{language === 'sv' ? 'Företag' : 'Company'} *</Label>
                    <Input 
                      required
                      value={formData.company}
                      onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                      className="bg-white border-stone-300 rounded-none"
                    />
                  </div>
                  <div>
                    <Label>{language === 'sv' ? 'Organisationsnummer' : 'Organization number'}</Label>
                    <Input 
                      value={formData.orgNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, orgNumber: e.target.value }))}
                      placeholder="XXXXXX-XXXX"
                      className="bg-white border-stone-300 rounded-none"
                    />
                  </div>
                  <div>
                    <Label>{language === 'sv' ? 'Namn' : 'Name'} *</Label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-white border-stone-300 rounded-none"
                    />
                  </div>
                  <div>
                    <Label>E-post *</Label>
                    <Input 
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-white border-stone-300 rounded-none"
                    />
                  </div>
                  <div>
                    <Label>{language === 'sv' ? 'Telefon' : 'Phone'}</Label>
                    <Input 
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-white border-stone-300 rounded-none"
                    />
                  </div>
                  <div>
                    <Label>{language === 'sv' ? 'Meddelande' : 'Message'}</Label>
                    <Textarea 
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder={language === 'sv' ? 'Eventuella frågor eller önskemål...' : 'Any questions or requests...'}
                      className="bg-white border-stone-300 rounded-none"
                      rows={3}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-none py-3"
                  >
                    {submitting ? '...' : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {language === 'sv' ? 'Skicka offertförfrågan' : 'Send quote request'}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteListPage;
